import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/Badges";
import DueByPill from "@/components/invoices/DueByPill";
import { InlineSpinner } from "@/components/Spinner";
import {
  Building2, Calendar, Wallet, Info, User,
  FileText, Download, Check,
} from "lucide-react";
import { api, INR, formatApiError, downloadFile, fmtDMY } from "@/lib/api";
import { toast } from "sonner";

function PaymentTimeline({ detail, sortedPayments }) {
  return (
    <div className="space-y-3" data-testid="payment-history">
      <div className="text-sm tabular flex items-baseline gap-3">
        <span className="font-semibold">{INR(detail.paid_amount || 0)}</span>
        <span className="text-ink-2 text-[11px]">paid of {INR(detail.amount)}</span>
        {detail.status === "partial" && (
          <span className="ml-auto text-[11px] font-semibold text-[color:var(--pending)]">
            {INR(detail.remaining_amount)} pending
          </span>
        )}
      </div>
      <ol className="relative ml-2 border-l border-line space-y-3">
        {sortedPayments.map((p, idx) => (
          <li key={p.id || idx} className="pl-4 relative" data-testid={`payment-entry-${idx}`}>
            <span className="absolute -left-[5px] top-1.5 w-2 h-2 rounded-full bg-[color:var(--aisensy-green)]" />
            <div className="text-sm tabular font-semibold">
              {INR(p.amount)}
              <span className="text-ink-2 font-normal text-[11px] ml-2">
                on {fmtDMY(p.payment_date)}
              </span>
            </div>
            <div className="text-[11px] text-ink-2 tabular mt-0.5">
              {p.utr_number && <>UTR <span className="text-ink font-medium">{p.utr_number}</span></>}
              {p.utr_number && p.payment_id ? " · " : ""}
              {p.payment_id && <>Payment ID <span className="text-ink font-medium">{p.payment_id}</span></>}
              {p.recorded_by_name && (
                <span className="block text-ink-2/80">recorded by {p.recorded_by_name}</span>
              )}
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

function PaymentPanel({ detail, sortedPayments }) {
  if (detail.payments && detail.payments.length > 0) {
    return <PaymentTimeline detail={detail} sortedPayments={sortedPayments} />;
  }
  if (detail.status === "paid") {
    return (
      <div className="text-sm tabular space-y-0.5">
        {detail.utr_number && <div>UTR: <span className="font-semibold">{detail.utr_number}</span></div>}
        {detail.payment_id && <div>Payment ID: <span className="font-semibold">{detail.payment_id}</span></div>}
        {detail.payment_date && <div>Paid on {fmtDMY(detail.payment_date)}</div>}
      </div>
    );
  }
  return <div className="text-sm text-ink-2">Not paid yet</div>;
}

function PdfPanel({ detail }) {
  if (!detail.pdf_meta) {
    return (
      <div className="border border-dashed border-line rounded-sm p-4 text-xs text-ink-2">
        No PDF attached to this invoice.
      </div>
    );
  }
  const meta = detail.pdf_meta;
  const onDownload = async (e) => {
    e.preventDefault();
    try {
      await downloadFile(detail.pdf_file_id, meta.original_filename || `${detail.invoice_number}.pdf`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  return (
    <div className="border border-line rounded-sm bg-card p-4 flex items-center justify-between gap-4">
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-9 h-9 rounded-sm bg-[color:var(--aisensy-green-soft)] text-[color:var(--aisensy-dark)] grid place-items-center">
          <FileText className="w-4 h-4" />
        </div>
        <div className="min-w-0">
          <div className="text-sm font-semibold truncate">
            {meta.original_filename || "invoice.pdf"}
          </div>
          <div className="text-[11px] text-ink-2 tabular">
            {meta.size ? `${(meta.size / 1024).toFixed(1)} KB` : ""}
            {meta.uploaded_by_user ? ` · uploaded by ${meta.uploaded_by_user.name}` : ""}
            {meta.uploaded_at ? ` on ${meta.uploaded_at.slice(0, 10)}` : ""}
          </div>
        </div>
      </div>
      <a
        href="#"
        onClick={onDownload}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm bg-[color:var(--aisensy-dark)] hover:bg-[color:var(--aisensy-dark-2)] text-white text-xs font-semibold"
        data-testid="detail-download-pdf"
      >
        <Download className="w-3.5 h-3.5" /> Download PDF
      </a>
    </div>
  );
}

async function exportPaymentLedger(detail) {
  try {
    const res = await api.get(`/invoices/${detail.id}/payments/export`, { responseType: "blob" });
    const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `payments_${detail.invoice_number || detail.id}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  } catch (e) {
    toast.error(formatApiError(e));
  }
}

export default function InvoiceDetailDialog({
  open,
  onOpenChange,
  detail,
  detailLoading,
  sortedPayments,
  onEdit,
  onMarkPaid,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-[95vw] sm:max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-3">
            <span>Invoice {detail?.invoice_number || ""}</span>
            {detail && <StatusBadge status={detail.status} isOverdue={detail.is_overdue} />}
            {detail && <DueByPill invoice={detail} />}
          </DialogTitle>
        </DialogHeader>
        {detailLoading || !detail ? (
          <InlineSpinner label="Loading invoice" />
        ) : (
          <div className="space-y-5">
            <div className="grid grid-cols-3 gap-px bg-[color:var(--line)] border border-line rounded-sm overflow-hidden">
              <div className="bg-card p-4">
                <div className="eyebrow">Amount</div>
                <div className="tabular text-2xl font-semibold mt-1">{INR(detail.amount)}</div>
              </div>
              <div className="bg-card p-4">
                <div className="eyebrow">Invoice Date</div>
                <div className="tabular text-lg mt-1">{detail.invoice_date}</div>
              </div>
              <div className="bg-card p-4">
                <div className="eyebrow">Due Date</div>
                <div className="tabular text-lg mt-1">{detail.due_date}</div>
              </div>
            </div>

            <div className="border border-line rounded-sm bg-card p-4">
              <div className="flex items-center gap-2 eyebrow mb-2">
                <Building2 className="w-3.5 h-3.5" /> {detail.entity_type === "client" ? "Client" : "Partner"}
              </div>
              <div className="font-semibold text-lg">{detail.entity?.name || detail.entity_name}</div>
              <div className="grid grid-cols-2 gap-2 mt-2 text-xs text-ink-2 tabular">
                <div>{detail.entity?.email || ""}</div>
                <div>{detail.entity?.phone || ""}</div>
                {detail.entity?.gstin && <div>GSTIN {detail.entity.gstin}</div>}
                {detail.entity?.payment_timeline_days && (
                  <div>{detail.entity.payment_timeline_days}-day payment terms</div>
                )}
              </div>
              {detail.entity?.account_manager && (
                <div className="mt-3 pt-3 border-t border-line flex items-center gap-2 text-xs">
                  <div className="w-6 h-6 rounded-full bg-[color:var(--aisensy-green-soft)] text-[color:var(--aisensy-dark)] grid place-items-center text-[10px] font-semibold">
                    {detail.entity.account_manager.name?.[0]?.toUpperCase()}
                  </div>
                  <div>
                    <div className="text-ink font-semibold">
                      Account Manager: {detail.entity.account_manager.name}
                    </div>
                    <div className="text-ink-2 tabular">{detail.entity.account_manager.email}</div>
                  </div>
                </div>
              )}
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="border border-line rounded-sm bg-card p-4">
                <div className="flex items-center gap-2 eyebrow mb-2">
                  <Calendar className="w-3.5 h-3.5" /> Billing Period
                </div>
                <div className="tabular text-sm">
                  {fmtDMY(detail.billing_period_start)} → {fmtDMY(detail.billing_period_end)}
                </div>
              </div>
              <div className="border border-line rounded-sm bg-card p-4">
                <div className="flex items-center gap-2 eyebrow mb-2">
                  <Wallet className="w-3.5 h-3.5" /> Payment
                </div>
                <PaymentPanel detail={detail} sortedPayments={sortedPayments} />
              </div>
            </div>

            {detail.notes && (
              <div className="border border-line rounded-sm bg-card p-4">
                <div className="flex items-center gap-2 eyebrow mb-2">
                  <Info className="w-3.5 h-3.5" /> Notes
                </div>
                <div className="text-sm whitespace-pre-wrap">{detail.notes}</div>
              </div>
            )}

            <PdfPanel detail={detail} />

            <div className="text-[11px] text-ink-2 tabular flex items-center gap-2 pt-3 border-t border-line">
              <User className="w-3 h-3" />
              Created
              {detail.created_by_user
                ? <> by <span className="text-ink font-semibold">{detail.created_by_user.name}</span> ({detail.created_by_user.email})</>
                : ""}
              {detail.created_at ? ` on ${detail.created_at.slice(0, 10)}` : ""}
            </div>
          </div>
        )}
        <DialogFooter className="gap-2">
          {detail && detail.payments && detail.payments.length > 0 && (
            <Button
              variant="outline"
              onClick={() => exportPaymentLedger(detail)}
              className="rounded-sm"
              data-testid="detail-export-payments"
            >
              <Download className="w-4 h-4 mr-1.5" /> Export Ledger
            </Button>
          )}
          {detail && (detail.status === "unpaid" || detail.status === "partial") && (
            <Button
              onClick={() => { onOpenChange(false); onMarkPaid(detail); }}
              className="rounded-sm bg-[color:var(--aisensy-dark)] hover:bg-[color:var(--aisensy-dark-2)] text-white"
              data-testid="detail-mark-paid"
            >
              <Check className="w-4 h-4 mr-1.5" /> {detail.status === "partial" ? "Add Payment" : "Mark Paid"}
            </Button>
          )}
          {detail && (
            <Button
              variant="outline"
              onClick={() => { onOpenChange(false); onEdit(detail); }}
              className="rounded-sm"
              data-testid="detail-edit"
            >
              Edit
            </Button>
          )}
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-sm">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
